import { SyntheticEvent } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, Divider } from '@mui/material';

import { AgentStatus } from '@bandido/shared/constants';

import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';

interface UserStatusMenuProps {
  userId: string;
  queryKeys: Array<keyof typeof QueryKeys>;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const UserStatusMenu = ({ onClose, anchorEl, userId, queryKeys }: UserStatusMenuProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();

  const open = Boolean(anchorEl);

  const handleUserStatusChange = async (event: SyntheticEvent) => {
    const status = (event.target as HTMLDivElement).id;
    if (!status) return;

    try {
      await client.post(`user/${userId}/change-status`, {
        status,
      });
      queryKeys.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
      onClose();
    } catch (_e) {
      addToast({
        message: t('common.error.server'),
        color: 'error',
      });
    }
  };

  return (
    <Menu
      onClick={handleUserStatusChange}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      <MenuItem id={AgentStatus.AVAILABLE}>{t(`agentStatus.${AgentStatus.AVAILABLE}`)}</MenuItem>
      <Divider />
      <MenuItem id={AgentStatus.AFTER_CALL}>{t(`agentStatus.${AgentStatus.AFTER_CALL}`)}</MenuItem>
      <MenuItem id={AgentStatus.BREAK}>{t(`agentStatus.${AgentStatus.BREAK}`)}</MenuItem>
      <MenuItem id={AgentStatus.LUNCH}>{t(`agentStatus.${AgentStatus.LUNCH}`)}</MenuItem>
      <Divider />
      <MenuItem id={AgentStatus.IN_CALL}>{t(`agentStatus.${AgentStatus.IN_CALL}`)}</MenuItem>
      <MenuItem id={AgentStatus.WORKING_IN_CASE}>{t(`agentStatus.${AgentStatus.WORKING_IN_CASE}`)}</MenuItem>
      <Divider />
      <MenuItem id={AgentStatus.NOT_AVAILABLE}>{t(`agentStatus.${AgentStatus.NOT_AVAILABLE}`)}</MenuItem>
      <Divider />
      <MenuItem id={AgentStatus.DISCONNECTED}>{t(`agentStatus.${AgentStatus.DISCONNECTED}`)}</MenuItem>
    </Menu>
  );
};

export default UserStatusMenu;
